import React, { useState, useCallback, useContext, useMemo, useEffect } from 'react';
import Countdown from 'react-countdown';
import BigNumber from 'bignumber.js';
import  { harvestPool } from '../../../blockchain/pools';

import { ToastContext } from '../../../context/toast';

const pad = (num) => {
  if (num < 10) {
    return `0${num}`;
  }

  return num;
}

const renderer = ({ days, hours, minutes }) => {
  return (
    <span>
      { days > 0 ? `${days} days ` : '' }{ pad(hours) }:{ pad(minutes) }
    </span>
  );
};

const PoolHarvestSection = ({ poolData }) => {
  const { addToast } = useContext(ToastContext);

  const [pendingHarvestTx, setPendingHarvestTx] = useState(false);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const updateTime = () => {
      const currentTime = Date.now() / 1000;
      const userNextHarvestUntil = new BigNumber(poolData.userNextHarvestUntil);

      setEnabled(prevState => poolData.canHarvest || userNextHarvestUntil.lt(currentTime));
    }

    const intervalId = setInterval(updateTime, 1000);

    updateTime();

    return () => clearInterval(intervalId);
  }, [poolData.canHarvest, poolData.userNextHarvestUntil, setEnabled]);

  const earned = () => {
    const rawEarnedValue = new BigNumber(poolData.userPendingReward)
      .div(new BigNumber(10).pow(poolData.rewardTokenDecimals));

    const boostEarnedValue = rawEarnedValue.times(poolData.selectedNft?.boostStake || 0).div(100);

    const earnedValue = rawEarnedValue.minus(boostEarnedValue);

    return earnedValue.toFormat(earnedValue.eq(0) || earnedValue.gt(1) ? 2 : 6);
  }

  const canHarvest = useMemo(() => {
    return new BigNumber(poolData.userPendingReward).gt(0) && enabled;
  }, [poolData.userPendingReward, enabled]);

  const handleHarvest = async () => {
    let tx;
    try {
      setPendingHarvestTx(true);
      tx = await harvestPool(poolData.address);
      await tx.wait();
      addToast('Harvest succeded', 'is-success');
    } catch (error) {
      tx = { error: error.data?.message || error.message };
    }
    if (tx.error !== undefined) {
      console.log('error', tx.error);
      addToast('Harvest failed', 'is-danger');
    }

    setPendingHarvestTx(false);
  }

  const renderHarvestOrCountDown = useMemo(() => {
    if (enabled) {
      return <>HARVEST</>;
    }

    return <Countdown date={ new BigNumber(poolData.userNextHarvestUntil).times(1000).toNumber() } />
  }, [poolData.userNextHarvestUntil, enabled]);

  const harvestLockup = useCallback(() => {
    let lockup = new BigNumber(poolData.harvestInterval);

    if (poolData.hasNFT) {
      const experience = new BigNumber(poolData.selectedNft.experience);
      lockup = lockup.times(0.75).minus(experience.div(10));
    }

    return (
      <>
        <span className={ `has-text-danger ${poolData.hasNFT ? 'is-line-through mr-1' : ''}` }>
          <Countdown date={ new BigNumber(poolData.harvestInterval).times(1000).plus(Date.now()).toNumber() } autoStart={ false } renderer={ renderer } />
        </span>
        {poolData.hasNFT ? (
          <span className="has-text-warning">
            <Countdown date={ lockup.times(1000).plus(Date.now()).toNumber() } autoStart={ false } renderer={ renderer } />
          </span>
        ) : null}
      </>
    );
  }, [poolData.harvestInterval, poolData.hasNFT, poolData.selectedNft?.experience]);

  return (
    <>
      <small className="is-flex is-justify-content-space-between">
        <span><span className="has-text-success">{ poolData.rewardToken.toUpperCase() }</span> EARNED</span>
        <span>LOCKUP { harvestLockup() }</span>
      </small>
      <div className="level is-mobile">
        <div className="level-left">
          <div className="level-item">
            <h5 className="is-size-5 has-text-weight-semibold">
              { earned() }
            </h5>
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <div className="is-flex is-flex-direction-column">
              <button
                disabled={ pendingHarvestTx || !canHarvest }
                className={ `button is-success ${pendingHarvestTx ? 'is-loading' : ''}` }
                onClick={ handleHarvest }
              >
                { renderHarvestOrCountDown }
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default PoolHarvestSection;
